import React from 'react';
import {
  BoldOutlined,
  ItalicOutlined,
  LetterSpacingOutlined,
  LineHeightOutlined,
} from '@easyv/react-icons';
import { set } from 'lodash-es';
import { FontSelection } from '@/baseui/FontSelection';
import { deepClone } from '@/utils';
import { ToggleStringIcon } from '@/baseui/ToggleStringIcon';
import { MultiTypeColor } from '@/type';
import { ProInputNumber } from './ProInputNumber';
import { ImmerFormItem } from './ImmerFormItem';
import { ProColorsPicker } from './ProColorsPicker';
import { ProInputPercentNumber } from './ProInputPercentNumber';
import { formContentLWidth1, formContentRWidth1 } from '@/constants';

export type TextAlign = 'left' | 'center' | 'right';
export type FontWeight = 'bold' | 'normal';
export type FontStyle = 'italic' | 'normal';

export type TextStyle = {
  textAlign?: TextAlign;
  fontFamily: string;
  fontSize: number;
  fontColor: MultiTypeColor;
  fontOpacity: number;
  fontWeight: FontWeight;
  fontStyle: FontStyle;
  letterSpacing: number;
  lineHeight: number;
};

interface TextStyleProps {
  disabled?: boolean;
  label: string;
  tooltip: React.ReactNode;
  value: TextStyle;
  name: string;
  onChange: (name: string, value: unknown) => void;
  onBlur: (name: string, value: unknown) => void;
}

export const TextStyleGroup: React.FC<TextStyleProps> = (props) => {
  const { disabled, label, tooltip, value, name, onChange, onBlur } = props;

  const handleChange = (n: string, v: unknown) => {
    const newValue = deepClone(value);
    set(newValue, n, v);

    onChange(name, newValue);
  };

  const handleBlur = (n: string, v: unknown) => {
    const newValue = deepClone(value);
    set(newValue, n, v);
    onBlur(name, newValue);
  };

  return (
    <>
      <ImmerFormItem label={label} tooltip={tooltip}>
        <div className='flex items-center gap-x-2'>
          {/* 不加 overflow-hidden 宽度就不是预期宽度 */}
          <div className='relative overflow-hidden' style={{ width: formContentLWidth1 }}>
            <FontSelection
              disabled={disabled}
              value={value.fontFamily}
              onChange={(value) => {
                handleChange('fontFamily', value);
              }}
            />
          </div>
          <div style={{ width: formContentRWidth1 }}>
            <ProInputNumber
              disabled={disabled}
              value={value.fontSize}
              name='fontSize'
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>
        </div>
      </ImmerFormItem>
      <ImmerFormItem label=' '>
        <div className='flex items-center gap-x-2'>
          <div style={{ width: formContentLWidth1 }}>
            <ProColorsPicker
              disabledAlpha
              type={['pure']}
              value={value.fontColor}
              onChange={(v) => {
                handleChange('fontColor', v);
              }}
              onChangeComplete={(v) => {
                handleBlur('fontColor', v);
              }}
            />
          </div>
          <div style={{ width: formContentRWidth1 }}>
            <ProInputPercentNumber
              disabled={disabled}
              value={value.fontOpacity}
              name='fontOpacity'
              onChange={handleChange}
              onBlur={handleBlur}
              onPointMoveStart={handleChange}
              onPointMoveEnd={handleBlur}
            />
          </div>
        </div>
      </ImmerFormItem>
      <ImmerFormItem label=' '>
        <div className='flex items-center gap-x-2'>
          <ToggleStringIcon
            disabled={disabled}
            icon={<BoldOutlined size={14} />}
            enableString='bold'
            disableString='normal'
            value={value.fontWeight}
            onChange={(v) => {
              handleChange('fontWeight', v);
            }}
          />

          <ToggleStringIcon
            disabled={disabled}
            icon={<ItalicOutlined size={14} />}
            enableString='italic'
            disableString='normal'
            value={value.fontStyle}
            onChange={(v) => {
              handleChange('fontStyle', v);
            }}
          />

          <ProInputNumber
            disabled={disabled}
            value={value.letterSpacing}
            name='letterSpacing'
            prefix={<LetterSpacingOutlined />}
            onChange={handleChange}
            onBlur={handleBlur}
          />

          <ProInputNumber
            disabled={disabled}
            value={value.lineHeight}
            name='lineHeight'
            prefix={<LineHeightOutlined />}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
      </ImmerFormItem>
    </>
  );
};
